import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PageMeta from '../SEO/PageMeta';
import { Search, FileText, Newspaper, Play, ArrowLeft, ArrowRight } from 'lucide-react';
import axios from 'axios';
import NewsArticleReader from '../News/NewsArticleReader';
import DailyMockTest from './DailyMockTest';
import logoImg from '../../assets/logo.png';
import templeImg from '../../assets/temple-far1.jpg'; // save the new wide temple image here
import './LandingPage.css';

const HOME_TITLE = 'Free UPSC Preparation: PYQs, Current Affairs & Prelims Practice | CivilFox';
const HOME_DESCRIPTION = 'Prepare for UPSC Civil Services with previous-year questions from supported exams, current-affairs analysis, daily Prelims practice, flashcards, spaced revision, and a video syllabus.';

/* ─── SHARED ──────────────────────────────────────────── */
const publicApi = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'https://civilfox-backend.onrender.com/api' });
const VIEWS_KEY = 'civilfox_guest_views';
const TOKEN_KEY = 'vidyacards_token';
const GUEST_LIMIT = 5;
const PAGE_SIZE = 20;

const views = () => { try { return JSON.parse(localStorage.getItem(VIEWS_KEY) || '[]'); } catch { return []; } };

const CARDS = [
  { to: '/pyq-search', title: 'Search PYQ', note: 'Past exam questions', Icon: Search, tone: 'sage' },
  { to: '/mock-test', title: 'Mock Test', note: "Today's prelims", Icon: FileText, tone: 'teal' },
  { to: '/news', title: 'Read News', note: 'UPSC-filtered', Icon: Newspaper, tone: 'rust' },
  { to: '/study-upsc-syllabus', title: 'Study Syllabus', note: 'Topic-wise practice', Icon: Play, tone: 'gold' },
];

/* ─── HERO ────────────────────────────────────────────── */
function Hero() {
  return (
    <header className="cf-hero" id="top">
      <img className="cf-hero-img" src={templeImg} alt="" />

      <nav className="cf-nav" aria-label="Main">
        <Link to="/" className="cf-brand" aria-label="CivilFox home">
          <img src={logoImg} alt="" />
          <span>CivilFox</span>
        </Link>
        <div className="cf-nav-links">
          <Link to="/pyq-search">PYQ Search</Link>
          <Link to="/mock-test">Mock Test</Link>
          <Link to="/news">News</Link>
        </div>
        <div className="cf-nav-end">
          <Link to="/login" className="cf-nav-login">Login</Link>
          <Link to="/register" className="cf-btn rust sm">Start free</Link>
        </div>
      </nav>

      <div className="cf-hero-copy">
        <h1>Free UPSC preparation, built around how you study</h1>
        <p>Mussoorie is calling. Practise PYQs, follow current affairs, take daily Prelims tests, and revise with flashcards.</p>
      </div>

      <div className="cf-cards">
        {CARDS.map(({ to, title, note, Icon, tone }) => {
          const inner = (
            <>
              <Icon size={26} strokeWidth={2} />
              <span className="cf-card-text"><strong>{title}</strong><small>{note}</small></span>
            </>
          );
          return <Link key={title} to={to} className={`cf-card ${tone}`}>{inner}</Link>;
        })}
      </div>
    </header>
  );
}

/* ─── 1 · SEARCH PYQ ──────────────────────────────────── */
function PyqSearch() {
  const [q, setQ] = useState('');
  const [res, setRes] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const timer = useRef(null);

  const run = async v => {
    if (!v.trim()) { setRes([]); setDone(false); return; }
    setBusy(true);
    try {
      const { data } = await publicApi.get('/public/search-questions', { params: { q: v } });
      setRes(data.results || []); setTotal(data.total || 0);
    } catch { setRes([]); }
    finally { setBusy(false); setDone(true); }
  };

  const onChange = e => {
    const v = e.target.value;
    setQ(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => run(v), 500);
  };

  return (
    <section id="pyq" className="cf-sec dark pyq">
      <h2 className="cf-title">Search PYQ</h2>
      <label className="cf-search">
        <Search size={20} />
        <input type="text" value={q} onChange={onChange} placeholder="Article 356, GST, Paris Agreement…" aria-label="Search past exam questions" autoComplete="off" />
        {busy && <i className="cf-spin" />}
      </label>

      {done && !busy && res.length === 0 && <p className="cf-sub">No questions found. Try a broader keyword.</p>}

      {res.length > 0 && (
        <>
          <p className="cf-sub">{total} found</p>
          <div className="cf-results">
            {res.slice(0, 10).map((r, n) => (
              <Link key={r._id || n} to={`/pyq-question/${r._id}`} className="cf-q">
                <div className="cf-meta">
                  <span>{r.exam}</span>
                  {r.year && <span>{r.year}</span>}
                  <span>{r.subject}</span>
                </div>
                {r.questionType === 'assertion-reason' && r.assertion && (
                  <><p className="cf-line">{r.assertion}</p><p className="cf-line">{r.reason}</p></>
                )}
                {r.questionType === 'statement' && r.statements?.length > 0 && (
                  <ol className="cf-stmts">{r.statements.map((st, i) => <li key={i}>{st}</li>)}</ol>
                )}
                <p className="cf-qtext">{r.question}</p>
                {r.options?.map((o, i) => (
                  <span key={i} className="cf-opt">({String.fromCharCode(65 + i)}) {o}</span>
                ))}
              </Link>
            ))}
          </div>
        </>
      )}
      <p className="cf-feature-link"><Link to="/pyq-search">Open the full PYQ search →</Link></p>
    </section>
  );
}

/* ─── 3 · READ NEWS ───────────────────────────────────── */
function News() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [i, setI] = useState(0);
  const [wall, setWall] = useState(false);
  const [page, setPage] = useState(1);
  const [more, setMore] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [total, setTotal] = useState(0);
  const touch = useRef(null);
  const authed = !!localStorage.getItem(TOKEN_KEY);

  const load = useCallback(async (p = 1, append = false) => {
    setFetching(true);
    try {
      const { data } = await publicApi.get('/news/live', { params: { limit: PAGE_SIZE, page: p } });
      const inc = data.articles || [];
      setItems(prev => (append ? [...prev, ...inc] : inc));
      setTotal(data.total || inc.length);
      setMore(p < (data.pages || 1));
    } catch { if (!append) setItems([]); }
    finally { setLoading(false); setFetching(false); }
  }, []);

  useEffect(() => { load(1, false); }, [load]);

  /* guest read limit */
  useEffect(() => {
    const cur = items[i];
    if (!cur || loading || authed) return;
    const seen = views();
    if (seen.includes(cur._id)) return;
    if (seen.length >= GUEST_LIMIT) { setWall(true); return; }
    localStorage.setItem(VIEWS_KEY, JSON.stringify([...seen, cur._id].slice(-50)));
  }, [i, items, loading, authed]);

  /* prefetch next page */
  useEffect(() => {
    if (!more || fetching || i < items.length - 5) return;
    const next = page + 1; setPage(next); load(next, true);
  }, [i, items.length, more, fetching, page, load]);

  const next = useCallback(() => {
    if (!localStorage.getItem(TOKEN_KEY) && views().length >= GUEST_LIMIT) { setWall(true); return; }
    setI(n => Math.min(items.length - 1, n + 1));
  }, [items.length]);
  const prev = useCallback(() => { setI(n => Math.max(0, n - 1)); setWall(false); }, []);

  useEffect(() => {
    const onKey = e => {
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev]);

  const onTouchStart = e => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchEnd = e => {
    const t = touch.current; touch.current = null;
    if (!t) return;
    const dx = e.changedTouches[0].clientX - t.x;
    const dy = e.changedTouches[0].clientY - t.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) (dx < 0 ? next : prev)();
  };

  const article = items[i];

  return (
    <section id="news" className="cf-sec dark wide">
      <h2 className="cf-title">Read News</h2>
      <p className="cf-sub">Today's UPSC-relevant stories</p>
      <p className="cf-feature-link"><Link to="/news">Browse UPSC current affairs →</Link></p>

      {loading ? (
        <div className="cf-state"><i className="cf-spin lg" /></div>
      ) : items.length === 0 ? (
        <p className="cf-sub">No stories yet. Check back soon.</p>
      ) : (
        <>
          <div className="cf-news-card" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            {wall ? (
              <div className="cf-wall">
                <h3>Sign in free to keep reading</h3>
                <Link to="/register" className="cf-btn rust">Create account</Link>
                <Link to="/login" className="cf-link">Log in</Link>
              </div>
            ) : article && (
              <NewsArticleReader article={article} onShare={a => publicApi.post(`/news/share/${a._id}`)} />
            )}
          </div>
          <div className="cf-pager">
            <button onClick={prev} disabled={i === 0} aria-label="Previous story"><ArrowLeft size={20} /></button>
            <span>{i + 1} / {total || items.length}</span>
            <button onClick={next} disabled={i === items.length - 1 && !more} aria-label="Next story"><ArrowRight size={20} /></button>
          </div>
        </>
      )}
    </section>
  );
}

/* ─── NEWSLETTER + COMMUNITY ──────────────────────────── */
const SOCIALS = [
  ['X', 'https://twitter.com/civilfox_in'],
  ['Instagram', 'https://instagram.com/civilfox_in'],
  ['LinkedIn', 'https://linkedin.com/company/civilfox'],
  ['WhatsApp', 'https://chat.whatsapp.com/REPLACE_WITH_LINK'],
  ['Slack', 'https://civilfox.slack.com'],
];

function Join() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [msg, setMsg] = useState('');

  const submit = async e => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    try {
      await publicApi.post('/public/newsletter', { email });
      setStatus('success'); setMsg("You're in. See you tonight."); setEmail('');
    } catch (err) {
      setStatus('error'); setMsg(err?.response?.data?.message || 'Something went wrong. Try again.');
    }
  };

  return (
    <section id="join" className="cf-sec cream">
      <h2 className="cf-title">Subscribe to our free newsletter</h2>
      {status === 'success' ? (
        <p className="cf-lead">{msg}</p>
      ) : (
        <form className="cf-form" onSubmit={submit}>
          <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" aria-label="Email for newsletter" />
          <button className="cf-btn rust" type="submit" disabled={status === 'loading'}>{status === 'loading' ? '…' : 'Subscribe'}</button>
        </form>
      )}
      {status === 'error' && <p className="cf-err" role="alert">{msg}</p>}
      <div className="cf-soc">
        {SOCIALS.map(([name, href]) => <a key={name} href={href} target="_blank" rel="noopener noreferrer">{name}</a>)}
      </div>
    </section>
  );
}

/* ─── FOOTER ──────────────────────────────────────────── */
function Footer() {
  return (
    <footer className="cf-foot">
      <Link to="/" className="cf-brand light"><img src={logoImg} alt="" /><span>CivilFox</span></Link>
      <div>
        <Link to="/pyq-search">Search PYQs</Link>
        <Link to="/mock-test">Mock tests</Link>
        <Link to="/news">Current affairs</Link>
        <Link to="/study-upsc-syllabus">Syllabus practice</Link>
        <Link to="/about">About</Link>
        <Link to="/contact">Contact</Link>
        <Link to="/privacy">Privacy</Link>
      </div>
      <small>© 2026 CivilFox</small>
    </footer>
  );
}

/* ─── PAGE ────────────────────────────────────────────── */
export default function LandingPage() {
  return (
    <div className="cf">
      <PageMeta
        title={HOME_TITLE}
        description={HOME_DESCRIPTION}
        path="/"
        structuredData={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'WebSite',
              name: 'CivilFox',
              url: 'https://civilfox.app/',
              description: HOME_DESCRIPTION,
            },
            {
              '@type': 'WebApplication',
              name: 'CivilFox',
              url: 'https://civilfox.app/',
              description: HOME_DESCRIPTION,
              applicationCategory: 'EducationApplication',
              operatingSystem: 'Web',
              offers: { '@type': 'Offer', price: '0', priceCurrency: 'INR' },
            },
          ],
        }}
      />
      <Hero />
      <PyqSearch />
      <DailyMockTest />
      <News />
      <Join />
      <Footer />
    </div>
  );
}